import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useCalls } from '../../api/telemetry'
import { StatusBadge } from '../shared/StatusBadge'
import { DirectionBadge } from '../shared/DirectionBadge'
import { Pagination } from '../shared/Pagination'
import { SpinLoader } from '../shared/SpinLoader'
import { ErrorCard } from '../shared/ErrorCard'
import { formatPhone, formatDuration, formatDateTime } from '../../lib/utils'

// Recent calls — Voysi flamingo light table. Visual reference:
// screens-1.jsx::RecentCallsTable (lines 203-242).

const PAGE_SIZE = 20

export function CallsTable() {
  const { t } = useTranslation('calls')
  const [offset, setOffset] = useState(0)
  const { data, isLoading, isError, refetch } = useCalls({ limit: PAGE_SIZE, offset })
  const navigate = useNavigate()

  const calls = data?.calls ?? []
  const total = data?.total ?? 0

  if (isLoading) return <SpinLoader label={t('history.loadingAll')} />
  if (isError) return <ErrorCard message={t('history.loadFailed')} onRetry={() => refetch()} />

  return (
    <div className="space-y-3">
      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-[12px] border border-ink-200 bg-white md:block">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr style={{ background: '#FAFAF8' }}>
              {[
                t('history.table.time'),
                t('history.table.phone'),
                t('history.table.direction'),
                t('history.table.status'),
                t('history.table.duration'),
                t('history.table.mode'),
              ].map((h) => (
                <th
                  key={h}
                  className="px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-ink-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-400">
                  {t('history.empty')}
                </td>
              </tr>
            ) : (
              calls.map((call) => {
                const phone =
                  call.direction === 'outbound' ? call.to_number : call.from_number
                return (
                  <tr
                    key={call.call_sid}
                    onClick={() => navigate(`/app/calls/${call.call_sid}`)}
                    className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50"
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-[12px] text-ink-500">
                      {formatDateTime(call.started_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/app/calls/stage/${encodeURIComponent(phone)}`)
                        }}
                        className="font-medium text-flamingo-500 hover:underline"
                      >
                        {formatPhone(phone)}
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      <DirectionBadge direction={call.direction} />
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={call.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[12px] text-ink-600">
                      {formatDuration(call.duration_s)}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-ink-500">{call.mode}</td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card layout */}
      <div className="space-y-2 md:hidden">
        {calls.length === 0 ? (
          <p className="py-8 text-center text-ink-400">{t('history.empty')}</p>
        ) : (
          calls.map((call) => {
            const phone =
              call.direction === 'outbound' ? call.to_number : call.from_number
            return (
              <div
                key={call.call_sid}
                onClick={() => navigate(`/app/calls/${call.call_sid}`)}
                className="cursor-pointer rounded-[12px] border border-ink-200 bg-white p-3 transition-colors hover:bg-ink-50"
              >
                <div className="flex items-center justify-between">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/app/calls/stage/${encodeURIComponent(phone)}`)
                    }}
                    className="font-medium text-flamingo-500 hover:underline"
                  >
                    {formatPhone(phone)}
                  </button>
                  <StatusBadge status={call.status} />
                </div>
                <div className="mt-2 flex items-center gap-3 text-xs text-ink-500">
                  <DirectionBadge direction={call.direction} />
                  <span className="font-mono">{formatDuration(call.duration_s)}</span>
                  <span className="ml-auto">{formatDateTime(call.started_at)}</span>
                </div>
              </div>
            )
          })
        )}
      </div>

      <Pagination offset={offset} limit={PAGE_SIZE} total={total} onPageChange={setOffset} />
    </div>
  )
}
